/**
 * useLiveAvatarConfig - Derived avatar model/config for live mode
 *
 * Extracts the linked-avatar resolution and shared embedded config so App.tsx
 * only consumes the live avatar data it needs.
 */

import { MutableRefObject, useMemo } from 'react';

import {
  getModelById,
  resolveModelUrlForQuality,
  type ModelConfig,
  type AvatarQuality
} from '../modelRegistry';
import { mergeAvatarRuntimeConfig } from '../avatarRuntimeConfig';
import type { CamlinAvatarConfig } from '../../sdk/CamlinAvatar';
import { useAvatarRuntimeConfig } from './useAvatarRuntimeConfig';

/**
 * Detect mobile WebKit / Android Chrome at hook init time. Used to force the
 * SD model on phones regardless of the project's `avatarQuality` setting,
 * because iOS Safari WKWebView has a tight WebGL memory budget that the HD
 * model (97 morph targets + 35MB GLB) blows past mid-render — the page is
 * killed by the OS before any error handler can intervene.
 *
 * Static at module load so we don't have to thread it through props or
 * re-derive on every render. SSR-safe: returns `false` when window is
 * undefined.
 */
const IS_MOBILE_USER_AGENT: boolean = (() => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) {
    return true;
  }
  // iPadOS 13+ pretends to be desktop Safari but exposes touch points.
  if ((navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1 && /Macintosh/.test(ua)) {
    return true;
  }
  return false;
})();

export interface UseLiveAvatarConfigOptions {
  selectedModel: ModelConfig;
  linkedAvatarModelId: string | null;
  linkedAvatarConfig: Record<string, unknown> | null;
  projectToken?: string | null;
  runtimeUrl?: string | null;
  requestedLiveVoiceId?: string;
  liveModeActive: boolean;
  isEmbeddedBuilderView: boolean;
  cameraZoom: number;
  isMobileCall: boolean;
  isWebrtcCall: boolean;
  isInVoiceCall: boolean;
  liveAvatarModelIdRef: MutableRefObject<string | null>;
  resolveLinkedAvatarModelId: (
    explicitAvatarModelId?: string | null,
    avatarConfig?: Record<string, unknown> | null
  ) => string | null;
}

export interface UseLiveAvatarConfigReturn {
  activeAvatarModel: ModelConfig;
  /**
   * Quality-resolved + mobile-overridden model URL, ready to hand to
   * `<Scene modelUrl={...} modelKey={...}>` or `<CamlinAvatar modelUrl>`.
   *
   * IMPORTANT: This is the value the renderer should consume — NOT
   * `activeAvatarModel.url`, which is always the HD URL regardless of
   * device. Routing the HD URL into a phone-class device's <Canvas> is
   * how iPhone Safari WKWebView ends up OOM-killing the page mid-load.
   * See AvatarSceneShell.tsx and App.tsx for the wiring.
   */
  resolvedModelUrl: string;
  /**
   * Merged appearance overrides for the avatar:
   *   project blueprint settings (skinColor / eyeColor / hairColor / backgroundColor / etc.)
   *   ⊕ model registry's `defaultAppearance` (per-model fallbacks)
   *
   * App.tsx must dispatch this as an `avatar:config:update` event once
   * the avatar reports ready (Avatar.tsx is the listener). The renderer
   * will NOT pick it up from props alone — the appearance pipeline runs
   * imperatively via window event so it can be replayed on model swap
   * without a full React rerender.
   *
   * Returns null when there are no overrides at all (use the GLB's
   * baked-in materials).
   */
  liveAvatarAppearance: Record<string, unknown> | null;
  effectiveCameraZoom: number;
  motionParallaxEnabled: boolean;
  effectiveSkipTeethAnimation: boolean | undefined;
  liveTtsVoiceId?: string;
  sharedLiveAvatarConfig: CamlinAvatarConfig;
  liveAvatarConfig: Record<string, unknown> | null;
}

export function useLiveAvatarConfig({
  selectedModel,
  linkedAvatarModelId,
  linkedAvatarConfig,
  projectToken,
  runtimeUrl,
  requestedLiveVoiceId,
  liveModeActive,
  isEmbeddedBuilderView,
  cameraZoom,
  isMobileCall,
  isWebrtcCall,
  isInVoiceCall,
  liveAvatarModelIdRef,
  resolveLinkedAvatarModelId
}: UseLiveAvatarConfigOptions): UseLiveAvatarConfigReturn {
  const resolvedLinkedAvatarModelId = useMemo(() => (
    resolveLinkedAvatarModelId(linkedAvatarModelId, linkedAvatarConfig)
  ), [linkedAvatarConfig, linkedAvatarModelId, resolveLinkedAvatarModelId]);

  const runtimeLinkedModel = useMemo(() => {
    if (!resolvedLinkedAvatarModelId) {
      return null;
    }

    return getModelById(resolvedLinkedAvatarModelId) ?? null;
  }, [resolvedLinkedAvatarModelId]);

  const activeAvatarModel = runtimeLinkedModel ?? selectedModel;
  liveAvatarModelIdRef.current = activeAvatarModel.id;

  const runtimeProjectToken = useMemo(() => {
    const linkedProjectToken = typeof linkedAvatarConfig?.projectToken === 'string'
      ? linkedAvatarConfig.projectToken.trim()
      : '';
    return linkedProjectToken || projectToken?.trim() || '__platform-default__';
  }, [linkedAvatarConfig, projectToken]);

  const {
    config: savedModelConfig
  } = useAvatarRuntimeConfig({
    enabled: Boolean(activeAvatarModel.id),
    projectToken: runtimeProjectToken,
    modelId: activeAvatarModel.id,
    runtimeUrl
  });

  const effectiveCameraZoom = useMemo(() => {
    if (isEmbeddedBuilderView) {
      return cameraZoom;
    }
    const linkedCameraZoom = linkedAvatarConfig?.cameraZoom;
    return typeof linkedCameraZoom === 'number' ? linkedCameraZoom : cameraZoom;
  }, [cameraZoom, isEmbeddedBuilderView, linkedAvatarConfig]);

  const motionParallaxEnabled = useMemo(() => {
    if (isEmbeddedBuilderView) {
      return false;
    }

    return linkedAvatarConfig?.offerGesture === true;
  }, [isEmbeddedBuilderView, linkedAvatarConfig]);

  const effectiveSkipTeethAnimation = useMemo(() => {
    const linkedSkipTeethAnimation = linkedAvatarConfig?.skipTeethAnimation;
    if (typeof linkedSkipTeethAnimation === 'boolean') {
      return linkedSkipTeethAnimation;
    }

    return liveModeActive ? true : undefined;
  }, [linkedAvatarConfig, liveModeActive]);

  const liveAvatarConfig = useMemo(() => {
    // Pull the project's appearance overrides (skin/eye/hair colours, background)
    // first, then fall back to the model registry's `defaultAppearance` for any
    // fields the project didn't set. Without the registry fallback, projects
    // that don't customise appearance render with whatever the GLB material
    // happens to ship with — which for several of our models is unusably wrong
    // (e.g. green eyes on a model that should have hazel). Ported from the
    // retired builder VisualChannel onReady handler (commit 1625edfe).
    const savedAppearance =
      savedModelConfig?.appearance && typeof savedModelConfig.appearance === 'object'
        ? { ...(savedModelConfig.appearance as Record<string, unknown>) }
        : {};

    const projectAppearance =
      linkedAvatarConfig?.appearance && typeof linkedAvatarConfig.appearance === 'object'
        ? { ...(linkedAvatarConfig.appearance as Record<string, unknown>) }
        : {};

    const projectFlatColors: Record<string, unknown> = {};
    if (linkedAvatarConfig && typeof linkedAvatarConfig.skinColor === 'string') {
      projectFlatColors.skinColor = linkedAvatarConfig.skinColor;
    }
    if (linkedAvatarConfig && typeof linkedAvatarConfig.eyeColor === 'string') {
      projectFlatColors.eyeColor = linkedAvatarConfig.eyeColor;
    }
    if (linkedAvatarConfig && typeof linkedAvatarConfig.hairColor === 'string') {
      projectFlatColors.hairColor = linkedAvatarConfig.hairColor;
    }
    if (linkedAvatarConfig && typeof linkedAvatarConfig.clothingColor === 'string') {
      projectFlatColors.clothingColor = linkedAvatarConfig.clothingColor;
    }
    if (linkedAvatarConfig && typeof linkedAvatarConfig.shirtColor === 'string') {
      projectFlatColors.clothingColor = linkedAvatarConfig.shirtColor;
    }

    const merged: Record<string, unknown> = {
      ...(savedModelConfig ?? {}),
      ...(linkedAvatarConfig ?? {}),
      appearance: {
      ...savedAppearance,
      ...projectFlatColors,
      ...projectAppearance
      },
    };

    return mergeAvatarRuntimeConfig({
      modelId: activeAvatarModel.id,
      savedConfig: savedModelConfig,
      hostOverrides: merged
    });
  }, [activeAvatarModel.id, linkedAvatarConfig, savedModelConfig]);

  const liveAvatarAppearance = liveAvatarConfig?.appearance ?? null;

  // Quality resolution. Project-level setting wins on desktop; on mobile we
  // force SD regardless because the HD model OOMs WKWebView (see
  // IS_MOBILE_USER_AGENT comment above). `linkedAvatarConfig?.avatarQuality`
  // is the per-flow setting from the builder Avatar settings modal.
  const requestedAvatarQuality: AvatarQuality =
    (linkedAvatarConfig?.avatarQuality as AvatarQuality) || 'sd';
  const avatarQuality: AvatarQuality = IS_MOBILE_USER_AGENT
    ? 'sd'
    : requestedAvatarQuality;
  if (IS_MOBILE_USER_AGENT && requestedAvatarQuality === 'hd' && typeof console !== 'undefined') {
    console.info('[useLiveAvatarConfig] Forcing SD avatar on mobile (project asked for HD)');
  }

  // Single source of truth for the URL the renderer should fetch. Computed
  // here so both render shells (the legacy `<CamlinAvatar>` shell via
  // `sharedLiveAvatarConfig.modelUrl` AND the runtime host's
  // `<Canvas>+<Scene>` path via the `resolvedModelUrl` return value) get the
  // same answer. Fix for mobile-blank-canvas: previously only
  // `sharedLiveAvatarConfig.modelUrl` was SD-aware, but App.tsx hard-codes
  // `useSharedLiveAvatarShell=false`, so that branch never ran and mobile
  // received the HD URL. Returning `resolvedModelUrl` separately lets
  // `AvatarSceneShell` consume the same SD URL on the active code path.
  const resolvedModelUrl = useMemo(
    () => resolveModelUrlForQuality(activeAvatarModel, avatarQuality),
    [activeAvatarModel, avatarQuality]
  );

  const sharedLiveAvatarConfig = useMemo<CamlinAvatarConfig>(() => ({
    mode: 'full',
    showChat: false,
    showButtons: false,
    showLogo: false,
    showAvatarSwitcher: false,
    modelUrl: resolvedModelUrl,
    cameraZoom: effectiveCameraZoom,
    motionParallaxEnabled,
    skipTeethAnimation: effectiveSkipTeethAnimation,
    isMobileCall,
    isWebrtcCall,
    isInVoiceCall,
    isLiveMode: liveModeActive,
    useDefaultMaterials: activeAvatarModel.textureMode === 'hybrid',
    useEmbeddedTextures: activeAvatarModel.textureMode === 'embedded',
    initialAppearance: liveAvatarAppearance as Record<string, string | undefined> | undefined,
    backgroundColor:
      typeof liveAvatarAppearance?.backgroundColor === 'string'
        ? liveAvatarAppearance.backgroundColor
        : undefined
  }), [
    activeAvatarModel,
    resolvedModelUrl,
    effectiveCameraZoom,
    motionParallaxEnabled,
    effectiveSkipTeethAnimation,
    isInVoiceCall,
    isMobileCall,
    isWebrtcCall,
    liveAvatarAppearance,
    liveModeActive
  ]);

  return {
    activeAvatarModel,
    resolvedModelUrl,
    liveAvatarAppearance,
    effectiveCameraZoom,
    motionParallaxEnabled,
    effectiveSkipTeethAnimation,
    liveTtsVoiceId: requestedLiveVoiceId || activeAvatarModel.voiceId,
    sharedLiveAvatarConfig,
    liveAvatarConfig
  };
}

export default useLiveAvatarConfig;
